import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Axios from 'axios';
import { apiEndpoints } from '../constants/api';
import { paths } from '../constants/paths';
import { CircularProgress, Box, Typography } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { AuthService } from '../services/auth-service';

const ResolveZohoCrmAccount: React.FC = () => {
  const { zohoCrmId } = useParams<{ zohoCrmId: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);

  // Get user authentication state
  const { user } = useAuth();

  useEffect(() => {
    const resolveAccount = async () => {
      if (!zohoCrmId) {
        setError('No Zoho CRM ID provided');
        return;
      }

      // Wait for authentication to be ready
      const token = AuthService.getToken();
      if (!user || !token) {
        // Authentication not ready yet, wait
        return;
      }

      if (isResolving) {
        // Prevent duplicate calls
        return;
      }

      setIsResolving(true);

      try {
        const response = await Axios.get<{
          accountId: string;
          accountName: string;
        }>(apiEndpoints.getAccountByZohoCrmId(zohoCrmId));

        // Redirect to the portfolio account page
        navigate(`${paths.portfolio}/${response.data.accountId}`, {
          replace: true,
        });
      } catch (err) {
        if (Axios.isAxiosError(err)) {
          if (err.response?.status === 404) {
            setError(`No account found for Zoho CRM ID: ${zohoCrmId}`);
          } else if (
            err.response?.status === 401 ||
            err.response?.status === 500
          ) {
            setError(
              'Authentication failed. Please refresh the page and try again.',
            );
          } else {
            setError(
              `Error resolving account: ${
                err.response?.data?.message || err.message
              }`,
            );
          }
        } else {
          setError('An unexpected error occurred while resolving the account');
        }
        setIsResolving(false);
      }
    };

    resolveAccount();
  }, [zohoCrmId, navigate, user, isResolving]);

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="50vh"
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  // Show loading while waiting for authentication or account resolution
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="50vh"
    >
      <CircularProgress />
      <Typography variant="body1" sx={{ mt: 2 }}>
        {!user || !AuthService.getToken()
          ? 'Waiting for authentication...'
          : 'Resolving account...'}
      </Typography>
    </Box>
  );
};

export default ResolveZohoCrmAccount;
